import { useState } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStore, faGift } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import VendorMarketplace from './VendorMarketplace';
import { updateGoalProgress, checkFirstTimeBadges } from '../../utils/goalTracking';

export default function ExecutePhase({ experienceId, experience }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const [tasks, setTasks] = useState(experience.tasks || []);
  const [showVendorSearch, setShowVendorSearch] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const getUserName = (userId) => {
    if (!userId || !family?.members) return 'Unknown';
    if (userId === user?.uid) return 'You';
    return family.onboarding?.partnerName || `Family Member`;
  };

  const toggleTaskComplete = async (taskId) => {
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    
    setTasks(updatedTasks);
    
    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        tasks: updatedTasks,
        updatedAt: serverTimestamp()
      });

      // Check if experience is fully complete
      const allComplete = updatedTasks.every(t => t.completed);
      if (allComplete && updatedTasks.length > 0) {
        toast.success('All tasks complete! Time to celebrate!');
        // Mark experience as completed
        const updatedExperience = {
          completed: true,
          completedDate: serverTimestamp()
        };
        await updateDoc(doc(db, 'projects', experienceId), updatedExperience);
        
        // Update goal progress and check for badges
        if (family?.id) {
          const experienceData = { ...experience, ...updatedExperience };
          await updateGoalProgress(family.id, experienceData);
          await checkFirstTimeBadges(family.id, experienceData);
        }
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const totalCount = tasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-4xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif font-bold text-text-primary mb-2">
              Bring It to Life
            </h1>
            <p className="text-text-secondary">
              Complete these tasks to make your {experience.typeName || experience.name} happen
            </p>
          </div>

          <button
            onClick={() => setShowVendorSearch(true)}
            className="px-6 py-3 bg-white border-2 border-border rounded-xl font-semibold hover:border-primary transition-all flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faStore} />
            Browse Vendors
          </button>
        </div>

        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-text-primary">
                Progress
              </span>
              <span className="text-sm font-semibold text-primary">
                {completedCount} / {totalCount} complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-primary to-primary-light h-3 rounded-full transition-all"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Task list */}
        {tasks.length > 0 ? (
          <div className="space-y-4">
            {tasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onToggle={() => toggleTaskComplete(task.id)}
                onFindVendor={() => {
                  setSelectedTask(task);
                  setShowVendorSearch(true);
                }}
                getUserName={getUserName}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl border-2 border-border">
            <p className="text-text-secondary mb-4">No tasks yet</p>
            <p className="text-sm text-text-secondary">
              Tasks will be generated from your decisions in the Discuss phase.
            </p>
          </div>
        )}

        {/* Add custom task */}
        <button
          onClick={() => {
            // Would open add task modal
            toast.success('Add task feature coming soon');
          }}
          className="w-full mt-6 py-4 bg-white border-2 border-dashed border-border rounded-xl font-semibold text-text-secondary hover:border-primary hover:text-primary transition-all"
        >
          + Add Custom Task
        </button>
      </div>

      {/* Vendor Marketplace */}
      {showVendorSearch && (
        <VendorMarketplace
          experienceId={experienceId}
          selectedTask={selectedTask}
          onSelectVendor={async (vendor, task) => {
            if (task && experienceId) {
              try {
                const experienceRef = doc(db, 'projects', experienceId);
                const experienceDoc = await getDoc(experienceRef);
                const experienceData = experienceDoc.data();
                const updatedTasks = (experienceData.tasks || []).map(t =>
                  t.id === task.id
                    ? { ...t, linkedVendor: vendor.id, linkedVendorName: vendor.name }
                    : t
                );

                await updateDoc(experienceRef, {
                  tasks: updatedTasks
                });

                setTasks(updatedTasks);
                toast.success(`Linked ${vendor.name} to "${task.title}"`);
                setShowVendorSearch(false);
                setSelectedTask(null);
              } catch (error) {
                console.error('Error linking vendor:', error);
                toast.error('Failed to link vendor');
              }
            }
          }}
          onClose={() => {
            setShowVendorSearch(false);
            setSelectedTask(null);
          }}
        />
      )}
    </div>
  );
}

function TaskCard({ task, onToggle, onFindVendor, getUserName }) {
  return (
    <motion.div
      whileHover={{ x: 4 }}
      className={`
        bg-white rounded-xl border-2 p-5 transition-all
        ${task.completed ? 'border-green-200 bg-green-50/50' : 'border-border'}
      `}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <button
          onClick={onToggle}
          className={`
            w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5
            ${task.completed 
              ? 'bg-green-500 border-green-500' 
              : 'border-border hover:border-primary'
            }
          `}
        >
          {task.completed && (
            <FontAwesomeIcon icon={faCheck} className="text-white text-sm" />
          )}
        </button>

        {/* Task content */}
        <div className="flex-1 min-w-0">
          <h3 className={`
            font-bold text-lg mb-1
            ${task.completed ? 'line-through text-text-secondary' : 'text-text-primary'}
          `}>
            {task.title}
          </h3>

          {/* Assigned to */}
          {task.assignedTo?.length > 0 && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-text-secondary">Assigned to:</span>
              {task.assignedTo.map(userId => (
                <span key={userId} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-semibold">
                  {getUserName(userId)}
                </span>
              ))}
            </div>
          )}

          {/* Due date */}
          {task.dueDate && (
            <p className="text-sm text-text-secondary mb-2">
              Due: {task.dueDate?.toDate ? format(task.dueDate.toDate(), 'MMM d, yyyy') : format(new Date(task.dueDate), 'MMM d, yyyy')}
            </p>
          )}

          {/* Linked vendor */}
          {task.linkedVendor && (
            <div className="flex items-center gap-2 mb-3">
              <FontAwesomeIcon icon={faStore} className="text-primary" />
              <span className="text-sm font-semibold text-primary">
                Vendor selected
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-3">
            {!task.linkedVendor && (
              <button
                onClick={onFindVendor}
                className="text-sm px-4 py-2 bg-primary/10 text-primary rounded-lg font-semibold hover:bg-primary/20 transition-colors"
              >
                Find a Vendor
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
